import React from "react";
import { useLang } from "../../context/LanguageContext";
import { Mail, Phone, ArrowUpRight } from "lucide-react";

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-8 py-5 border-t border-ink-800/15">
    <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 pt-1">
      {label}
    </dt>
    <dd className="text-base md:text-[17px] leading-relaxed text-ink-800">
      {value}
    </dd>
  </div>
);

export default function Team() {
  const { t, lang } = useLang();
  const p = t.team.partner1;

  const labels =
    lang === "bg"
      ? { education: "Образование", experience: "Опит", languages: "Езици", practice: "Практика", contact: "Контакт" }
      : lang === "it"
      ? { education: "Formazione", experience: "Esperienza", languages: "Lingue", practice: "Pratica", contact: "Contatto" }
      : { education: "Education", experience: "Experience", languages: "Languages", practice: "Practice", contact: "Contact" };

  return (
    <section
      id="team"
      data-testid="team-section"
      className="bg-cream-100 text-ink-800"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        {/* Section heading */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-24">
          <div className="md:col-span-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-ink-500 mb-6">
              {t.team.overline}
            </div>
            <h2
              data-testid="team-title"
              className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink-800"
            >
              {t.team.title}
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-base md:text-lg leading-relaxed text-ink-500 self-end">
            {t.team.lead}
          </p>
        </div>

        {/* Partner — editorial single layout */}
        <article data-testid="partner-0" className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT: huge serif name + Dickens-style pull quote */}
          <div className="lg:col-span-5">
            <h3 className="font-serif font-medium tracking-tight leading-[0.95] text-ink-800 text-balance">
              <span className="block text-5xl md:text-6xl lg:text-7xl">
                {p.name.split(/\s+/)[0]}
              </span>
              <span className="block italic text-5xl md:text-6xl lg:text-7xl mt-1 text-ink-700">
                {p.name.split(/\s+/).slice(1).join(" ")}
              </span>
            </h3>

            <div className="mt-10 max-w-md">
              <p className="font-serif italic text-lg md:text-xl leading-relaxed text-ink-500 border-l-2 border-crimson pl-5">
                {p.bio}
              </p>
            </div>
          </div>

          {/* RIGHT: structured detail list */}
          <div className="lg:col-span-7">
            <dl>
              <DetailRow label={labels.experience} value={p.experience.replace(/^(Опит|Experience|Esperienza):\s*/i, "")} />
              <DetailRow label={labels.languages} value={p.languages} />
              <DetailRow label={labels.practice} value={p.practice} />
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-8 py-5 border-t border-b border-ink-800/15">
                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 pt-1">
                  {labels.contact}
                </dt>
                <dd className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <a
                    href={`mailto:${p.email}`}
                    data-testid="partner-0-email"
                    className="group inline-flex items-center gap-2 text-base font-medium text-ink-800 hover:text-crimson transition-colors"
                  >
                    <Mail size={14} className="text-ink-500 group-hover:text-crimson transition-colors" />
                    {p.email}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                  <a
                    href={`tel:${p.phone.replace(/\s/g, "")}`}
                    data-testid="partner-0-phone"
                    className="group inline-flex items-center gap-2 text-base font-medium text-ink-800 hover:text-crimson transition-colors"
                  >
                    <Phone size={14} className="text-ink-500 group-hover:text-crimson transition-colors" />
                    {p.phone}
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </article>
      </div>
    </section>
  );
}
